import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from './layout/Layout';

interface User {
  id: number;
  name: string;
  email: string;
}

interface IssueType {
  id: number;
  name: string;
}

interface TicketResponse {
  id: number;
  message: string;
  responder_type: string;
  responder_name: string;
  created_at: string;
}

interface Ticket {
  id: number;
  user: User;
  issue_type: IssueType;
  message: string;
  is_resolved: boolean;
  is_seen_by_admin: boolean;
  resolved_at: string | null;
  created_at: string;
  last_response: {
    message: string;
    responder_type: string;
    created_at: string;
  } | null;
  responses?: TicketResponse[];
}

interface Stats {
  total: number;
  open: number;
  resolved: number;
  unseen: number;
}

const Support: React.FC = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [stats, setStats] = useState<Stats>({ total: 0, open: 0, resolved: 0, unseen: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTickets, setSelectedTickets] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [isSendingReply, setIsSendingReply] = useState(false);
  const [resolveOnReply, setResolveOnReply] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [ticketsRes, statsRes] = await Promise.all([
        axios.get('/admin/support/tickets', {
          params: {
            status: statusFilter === 'all' ? undefined : statusFilter,
            search: searchQuery || undefined,
            page: currentPage,
            limit: 20
          }
        }),
        axios.get('/admin/support/tickets/stats')
      ]);

      if (ticketsRes.data.success) {
        setTickets(ticketsRes.data.data.tickets);
        setTotalPages(ticketsRes.data.data.totalPages);
      }

      if (statsRes.data.success) {
        setStats(statsRes.data.data);
      }
    } catch (error) {
      console.error('Error loading support data:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchQuery, currentPage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleTicketClick = async (ticket: Ticket) => {
    try {
      const response = await axios.get(`/admin/support/tickets/${ticket.id}`);
      
      if (response.data.success) {
        setSelectedTicket(response.data.data.ticket);
        setReplyMessage('');
        setResolveOnReply(false);
        
        if (!ticket.is_seen_by_admin) {
          setTickets(tickets.map(t => 
            t.id === ticket.id ? { ...t, is_seen_by_admin: true } : t
          ));
          setStats({ ...stats, unseen: Math.max(0, stats.unseen - 1) });
        }
      }
    } catch (error) {
      console.error('Error loading ticket details:', error);
    }
  };

  const handleSendReply = async () => {
    if (!selectedTicket || !replyMessage.trim()) return;

    setIsSendingReply(true);

    try {
      const response = await axios.post(`/admin/support/tickets/${selectedTicket.id}/respond`, {
        message: replyMessage.trim(),
        resolve: resolveOnReply
      });

      if (response.data.success) {
        // Reload ticket details
        const detailsRes = await axios.get(`/admin/support/tickets/${selectedTicket.id}`);
        if (detailsRes.data.success) {
          setSelectedTicket(detailsRes.data.data.ticket);
          setReplyMessage('');
          setResolveOnReply(false);
          
          // Refresh ticket list
          await loadData();
        }
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('Failed to send reply');
    } finally {
      setIsSendingReply(false);
    }
  };

  const handleMarkSelectedAsSeen = async () => {
    if (selectedTickets.length === 0) return;

    try {
      await axios.post('/admin/support/tickets/mark-seen-multiple', {
        ticketIds: selectedTickets
      });
      
      await loadData();
      setSelectedTickets([]);
    } catch (error) {
      console.error('Error marking tickets as seen:', error);
    }
  };

  const toggleSelectTicket = (ticketId: number) => {
    setSelectedTickets(prev =>
      prev.includes(ticketId)
        ? prev.filter(id => id !== ticketId)
        : [...prev, ticketId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedTickets.length === tickets.length) {
      setSelectedTickets([]);
    } else {
      setSelectedTickets(tickets.map(t => t.id));
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 30) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Layout>
      {/* Page Title */}
      <div className="row">
          <div className="col-12">
            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
              <div>
                <h4 className="mb-1 font-size-24 fw-semibold" style={{ color: '#495057' }}>Support Tickets</h4>
                <p className="text-muted mb-0">Manage user support requests</p>
              </div>
              <div className="page-title-right">
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">
                    <a href="/dashboard" style={{ color: '#006175' }}>Nyvro</a>
                  </li>
                  <li className="breadcrumb-item active">Support</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="row mb-4">
          <div className="col-xl-3 col-md-6 mb-3">
            <div className="card stats-card h-100" style={{ cursor: 'pointer' }} onClick={() => setStatusFilter('all')}>
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <p className="text-muted mb-1 fw-medium">Total Tickets</p>
                    <h3 className="mb-0 fw-bold" style={{ color: '#006175' }}>{stats.total}</h3>
                    <small className="text-muted">All time</small>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-lg rounded-circle d-flex align-items-center justify-content-center" 
                         style={{ backgroundColor: '#00617520' }}>
                      <i className="bx bx-support font-size-24" style={{ color: '#006175' }}></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6 mb-3">
            <div className="card stats-card h-100" style={{ cursor: 'pointer' }} onClick={() => setStatusFilter('unseen')}>
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <p className="text-muted mb-1 fw-medium">New/Unseen</p>
                    <h3 className="mb-0 fw-bold" style={{ color: '#dc3545' }}>{stats.unseen}</h3>
                    <small className="text-danger">Needs attention</small>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-lg rounded-circle d-flex align-items-center justify-content-center" 
                         style={{ backgroundColor: '#dc354520' }}>
                      <i className="bx bx-bell font-size-24" style={{ color: '#dc3545' }}></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6 mb-3">
            <div className="card stats-card h-100" style={{ cursor: 'pointer' }} onClick={() => setStatusFilter('open')}>
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <p className="text-muted mb-1 fw-medium">Open</p>
                    <h3 className="mb-0 fw-bold" style={{ color: '#ffc107' }}>{stats.open}</h3>
                    <small className="text-warning">In progress</small>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-lg rounded-circle d-flex align-items-center justify-content-center" 
                         style={{ backgroundColor: '#ffc10720' }}>
                      <i className="bx bx-time font-size-24" style={{ color: '#ffc107' }}></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6 mb-3">
            <div className="card stats-card h-100" style={{ cursor: 'pointer' }} onClick={() => setStatusFilter('resolved')}>
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <p className="text-muted mb-1 fw-medium">Resolved</p>
                    <h3 className="mb-0 fw-bold" style={{ color: '#28a745' }}>{stats.resolved}</h3>
                    <small className="text-success">Completed</small>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-lg rounded-circle d-flex align-items-center justify-content-center" 
                         style={{ backgroundColor: '#28a74520' }}>
                      <i className="bx bx-check-circle font-size-24" style={{ color: '#28a745' }}></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                {/* Toolbar */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div className="d-flex gap-2">
                    <button
                      className={`btn ${statusFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'} btn-sm`}
                      onClick={() => { setStatusFilter('all'); setCurrentPage(1); }}
                    >
                      All
                    </button>
                    <button
                      className={`btn ${statusFilter === 'unseen' ? 'btn-danger' : 'btn-outline-danger'} btn-sm`}
                      onClick={() => { setStatusFilter('unseen'); setCurrentPage(1); }}
                    >
                      New ({stats.unseen})
                    </button>
                    <button
                      className={`btn ${statusFilter === 'open' ? 'btn-warning' : 'btn-outline-warning'} btn-sm`}
                      onClick={() => { setStatusFilter('open'); setCurrentPage(1); }}
                    >
                      Open
                    </button>
                    <button
                      className={`btn ${statusFilter === 'resolved' ? 'btn-success' : 'btn-outline-success'} btn-sm`}
                      onClick={() => { setStatusFilter('resolved'); setCurrentPage(1); }}
                    >
                      Resolved
                    </button>
                  </div>

                  <div className="d-flex gap-2 align-items-center">
                    {selectedTickets.length > 0 && (
                      <button 
                        className="btn btn-info btn-sm"
                        onClick={handleMarkSelectedAsSeen}
                      >
                        <i className='bx bx-check-circle me-1'></i>
                        Mark {selectedTickets.length} as Seen
                      </button>
                    )}
                    <div className="search-box" style={{ width: '250px' }}>
                      <div className="position-relative">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Search tickets..."
                          value={searchQuery}
                          onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setCurrentPage(1);
                          }}
                        />
                        <i className="bx bx-search position-absolute" 
                           style={{ right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#999' }}></i>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Table */}
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : tickets.length === 0 ? (
                  <div className="text-center py-5">
                    <i className='bx bx-support' style={{ fontSize: '64px', color: '#ccc' }}></i>
                    <p className="text-muted mt-3">No tickets found</p>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: '40px' }}>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={selectedTickets.length === tickets.length && tickets.length > 0}
                                onChange={toggleSelectAll}
                              />
                            </th>
                            <th>User</th>
                            <th>Issue Type</th>
                            <th>Message</th>
                            <th>Status</th>
                            <th>Created</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tickets.map((ticket) => (
                            <tr 
                              key={ticket.id}
                              style={{
                                backgroundColor: !ticket.is_seen_by_admin ? '#fff3e0' : 'transparent',
                                fontWeight: !ticket.is_seen_by_admin ? '600' : 'normal'
                              }}
                            >
                              <td>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={selectedTickets.includes(ticket.id)}
                                  onChange={() => toggleSelectTicket(ticket.id)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className="avatar-sm rounded-circle d-flex align-items-center justify-content-center me-2"
                                       style={{ background: '#e0e0e0', fontSize: '14px', fontWeight: '600', color: '#666', width: '36px', height: '36px' }}>
                                    {ticket.user.name.charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <div className="fw-medium">{ticket.user.name}</div>
                                    <small className="text-muted">{ticket.user.email}</small>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <span className="badge bg-primary">{ticket.issue_type.name}</span>
                              </td>
                              <td>
                                <div style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {ticket.message}
                                </div>
                                {ticket.last_response && (
                                  <small className="text-muted">
                                    <i className='bx bx-message-dots'></i> Last reply: {formatDate(ticket.last_response.created_at)}
                                  </small>
                                )}
                              </td>
                              <td>
                                <div className="d-flex flex-column gap-1">
                                  <span className={`badge ${ticket.is_resolved ? 'bg-success' : 'bg-warning'}`}>
                                    {ticket.is_resolved ? 'Resolved' : 'Open'}
                                  </span>
                                  {!ticket.is_seen_by_admin && (
                                    <span className="badge bg-danger" style={{ fontSize: '10px' }}>
                                      NEW
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <small className="text-muted">{formatDate(ticket.created_at)}</small>
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => handleTicketClick(ticket)}
                                >
                                  <i className='bx bx-show'></i> View
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-4">
                        <div className="text-muted">
                          Page {currentPage} of {totalPages}
                        </div>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(currentPage - 1)}
                          >
                            <i className='bx bx-chevron-left'></i> Previous
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage(currentPage + 1)}
                          >
                            Next <i className='bx bx-chevron-right'></i>
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title">Ticket #{selectedTicket.id}</h5>
                  <small className="text-muted">{selectedTicket.user.name} • {selectedTicket.issue_type.name}</small>
                </div>
                <button type="button" className="btn-close" onClick={() => setSelectedTicket(null)}></button>
              </div>
              <div className="modal-body" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {/* Ticket Status Info */}
                <div className="alert alert-light border mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className={`badge ${selectedTicket.is_resolved ? 'bg-success' : 'bg-warning'}`}>
                      {selectedTicket.is_resolved ? 'Resolved' : 'Open'}
                    </span>
                    <small className="text-muted">{new Date(selectedTicket.created_at).toLocaleString()}</small>
                  </div>
                </div>

                {/* Original Message */}
                <div className="mb-3">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <strong style={{ color: '#006175' }}>{selectedTicket.user.name}</strong>
                    <span className="badge bg-primary" style={{ fontSize: '10px' }}>USER</span>
                  </div>
                  <div className="p-3 rounded" style={{ backgroundColor: '#e3f2fd', borderLeft: '3px solid #006175' }}>
                    {selectedTicket.message}
                  </div>
                </div>

                {/* Responses */}
                {selectedTicket.responses && selectedTicket.responses.length > 0 ? (
                  <>
                    {selectedTicket.responses.map((response) => (
                      <div key={response.id} className="mb-3">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <strong style={{ color: response.responder_type === 'admin' ? '#28a745' : '#006175' }}>
                            {response.responder_name}
                          </strong>
                          <span className={`badge ${response.responder_type === 'admin' ? 'bg-success' : 'bg-info'}`} 
                                style={{ fontSize: '10px' }}>
                            {response.responder_type.toUpperCase()}
                          </span>
                          <small className="text-muted">{formatDate(response.created_at)}</small>
                        </div>
                        <div className="p-3 rounded" style={{
                          backgroundColor: response.responder_type === 'admin' ? '#e8f5e9' : '#f5f5f5',
                          borderLeft: `3px solid ${response.responder_type === 'admin' ? '#28a745' : '#6c757d'}`
                        }}>
                          {response.message}
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="text-center py-4">
                    <i className='bx bx-message-x text-muted' style={{ fontSize: '48px' }}></i>
                    <p className="text-muted mt-2">No responses yet</p>
                  </div>
                )}
              </div>
              
              {/* Reply Section */}
              {!selectedTicket.is_resolved && (
                <div className="border-top p-3" style={{ backgroundColor: '#f8f9fa' }}>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Reply to User</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      placeholder="Type your reply..."
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      disabled={isSendingReply}
                    />
                  </div>
                  <div className="form-check mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="resolveTicket"
                      checked={resolveOnReply}
                      onChange={(e) => setResolveOnReply(e.target.checked)}
                      disabled={isSendingReply}
                    />
                    <label className="form-check-label" htmlFor="resolveTicket">
                      Mark as Resolved after sending reply
                    </label>
                  </div>
                  <div className="d-flex justify-content-end gap-2">
                    <button 
                      type="button" 
                      className="btn btn-secondary" 
                      onClick={() => setSelectedTicket(null)}
                      disabled={isSendingReply}
                    >
                      Close
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-primary" 
                      onClick={handleSendReply}
                      disabled={isSendingReply || !replyMessage.trim()}
                    >
                      {isSendingReply ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Sending...
                        </>
                      ) : (
                        <>
                          <i className='bx bx-send me-1'></i>
                          Send Reply
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
              
              {selectedTicket.is_resolved && (
                <div className="modal-footer bg-light">
                  <div className="text-muted me-auto">
                    <i className="bx bx-lock-alt me-2"></i>
                    This ticket is resolved and closed
                  </div>
                  <button type="button" className="btn btn-secondary" onClick={() => setSelectedTicket(null)}>
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
  </Layout>
  );
};

export default Support;
